import React from 'react';
import type { OfferMilestone, OfferPayment } from '../types';
import { getOfferPaymentState, isOffPlatformPayment } from '../types';
import { formatMoney } from './offerFees';
import { sortPaymentsNewestFirst } from './paymentLinks';
import { Tooltip } from '../../../ui/Tooltip';
import { paymentStatusTips, paymentTagTips } from '../../../../onboarding/copy';

function formatPaymentDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const STATE_STYLE = {
  pending: 'bg-[#2a2010] text-[#fbbf24] border-[#4a3b1c]',
  confirmed: 'bg-[#0d2818] text-[#4ade80] border-[#1b432a]',
  rejected: 'bg-[#2d1416] text-[#f87171] border-[#4c1d24]',
} as const;

function statusLabel(payment: OfferPayment): string {
  const state = getOfferPaymentState(payment);
  const offPlatform = isOffPlatformPayment(payment);
  if (state === 'confirmed') return offPlatform ? 'Confirmed' : 'Paid';
  if (state === 'rejected') return offPlatform ? 'Rejected' : 'Not paid';
  return offPlatform ? 'Awaiting confirmation' : 'Awaiting payment';
}

interface OfferPaymentsListProps {
  payments: OfferPayment[];
  milestones: OfferMilestone[];
  currency: string;
}

export const OfferPaymentsList: React.FC<OfferPaymentsListProps> = ({
  payments,
  milestones,
  currency,
}) => {
  if (payments.length === 0) return null;
  const sorted = sortPaymentsNewestFirst(payments);

  return (
    <div className="rounded-3xl border border-[#22242a] bg-[#121316] p-4 sm:p-6 space-y-4 shadow-sm">
      <h3 className="text-sm font-bold text-white">Payments</h3>
      <ul className="space-y-2.5">
        {sorted.map((payment) => {
          const offPlatform = isOffPlatformPayment(payment);
          const state = getOfferPaymentState(payment);
          const milestoneTitles = (payment.milestoneIds ?? [])
            .map((id) => milestones.find((m) => Number(m.id) === id)?.title)
            .filter(Boolean)
            .join(', ');
          const meta = [formatPaymentDate(payment.createdAt), milestoneTitles]
            .filter(Boolean)
            .join(' · ');
          return (
            <li
              key={`${payment.id}-${payment.createdAt ?? ''}`}
              className="rounded-2xl border border-[#22242a] bg-[#0c0d0f] p-3.5 space-y-2"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Tooltip
                      content={offPlatform ? paymentTagTips.offPlatform : paymentTagTips.stripe}
                      touch="tap"
                    >
                      <span
                        tabIndex={0}
                        className={`inline-flex cursor-help items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${
                          offPlatform
                            ? 'bg-[#1f1a2e] text-[#c4b5fd] border-[#3b2f5c]'
                            : 'bg-[#182234] text-[#60a5fa] border-[#253956]'
                        }`}
                      >
                        {offPlatform ? 'Off-platform' : 'Stripe'}
                      </span>
                    </Tooltip>
                    <Tooltip
                      content={paymentStatusTips[statusLabel(payment).toLowerCase()]}
                      touch="tap"
                    >
                      <span
                        tabIndex={0}
                        className={`inline-flex cursor-help items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${STATE_STYLE[state]}`}
                      >
                        {statusLabel(payment)}
                      </span>
                    </Tooltip>
                  </div>
                  {meta && <p className="text-[11px] text-zinc-500">{meta}</p>}
                </div>
                <span
                  className={`text-sm font-bold tabular-nums flex-shrink-0 ${
                    state === 'rejected' ? 'text-zinc-500 line-through' : 'text-white'
                  }`}
                >
                  {formatMoney(payment.amount, currency)}
                </span>
              </div>
              {payment.note && (
                <p className="text-[11px] text-zinc-400 leading-relaxed whitespace-pre-wrap break-words">
                  {payment.note}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
